import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom "buy now" button: adds the current product form's selection to the
 * cart, then redirects straight to checkout. Used in place of Shopify's native
 * accelerated checkout button when the merchant wants to control the button's
 * label (the native button's text/branding isn't theme-controllable).
 *
 * @typedef {object} BuyNowButtonRefs
 * @property {HTMLButtonElement} buyNowButton
 * @extends Component<BuyNowButtonRefs>
 */
export class BuyNowButtonComponent extends Component {
  requiredRefs = ['buyNowButton'];

  /** @param {MouseEvent} event */
  async handleClick(event) {
    event.preventDefault();

    const { buyNowButton } = this.refs;
    if (buyNowButton.disabled) return;

    const form = this.closest('form');
    if (!form) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const checkoutUrl = this.dataset.checkoutUrl || '/checkout';

    buyNowButton.disabled = true;
    buyNowButton.classList.add('buy-now-button--loading');

    try {
      const formData = new FormData(form);
      // `Theme` is declared via `const` in a classic <script> (snippets/scripts.liquid), so
      // it lives in the realm's shared global environment, not as a `window` property — same
      // bare reference assets/product-form.js uses for this.
      const cartAddUrl = Theme.routes.cart_add_url;

      // 'javascript' (not 'json') is required here: fetchConfig deletes the Content-Type
      // header for that type, letting the browser set the multipart boundary FormData
      // needs. Forcing application/json with a FormData body breaks parsing server-side.
      const response = await fetch(
        cartAddUrl,
        fetchConfig('javascript', { body: formData, headers: { Accept: 'application/json' } })
      );

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.description || `Add to cart failed with status ${response.status}`);
      }

      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('[buy-now-button]', error);
      buyNowButton.disabled = false;
      buyNowButton.classList.remove('buy-now-button--loading');
      window.alert(this.dataset.errorMessage || 'Something went wrong, please try again.');
    }
  }
}

if (!customElements.get('buy-now-button')) {
  customElements.define('buy-now-button', BuyNowButtonComponent);
}
